import axios from 'axios';
import { getIdToken } from './auth';
import { retryRequest } from './vertexAI';
import { getAuth } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { isBusinessRelatedQuestion, findBestMatchingAnswer, getFallbackMessage } from './aiMatcher';

// IMPORTANT DEBUG MESSAGE - REMOVE AFTER FIXING
console.log('===============================================');
console.log('LOADED UPDATED aiChat.js WITH IMPROVED AI MATCHING ALGORITHM v3.0');
console.log('- Business keyword check added');
console.log('- Matching threshold increased to 5');
console.log('- Token matching percentage check added (30%)');
console.log('===============================================');

// Base URL for the API
const API_BASE_URL = '/api';

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Use environment variables or fallback to test numbers
const DEFAULT_PHONE = process.env.REACT_APP_DEFAULT_PHONE || '+15555555555';
const TWILIO_NUMBER = process.env.REACT_APP_TWILIO_NUMBER || '+19124173682';

// Get conversation history
export const getConversationHistory = async (conversationId) => {
  try {
    const response = await retryRequest(() => api.get(`/vertex-ai/conversation/${conversationId}`), 1, 1000);
    return response.data;
  } catch (error) {
    console.error('Error getting conversation history:', error);
    return { messages: [], error: true };
  }
};

/**
 * Creates a new conversation with the AI
 * @param {string} userId - The user ID
 * @param {string} phoneNumber - The phone number
 * @param {Object} businessData - The business data
 * @returns {Promise<Object>} - The conversation data
 */
export const createConversation = async (userId, phoneNumber, businessData) => {
  try {
    console.log('Creating conversation for user:', userId, 'with phone:', phoneNumber);
    console.log('Business data provided:', businessData);

    // Validate business data
    if (!businessData) {
      console.error('No business data provided');
      throw new Error('No business data provided');
    }

    // Extract and validate business information
    const businessName = businessData.businessInfo?.name || 'Your Business';
    const businessPhone = businessData.phoneData?.phoneNumber ||
                         businessData.phoneNumber ||
                         businessData.businessInfo?.phoneNumber ||
                         phoneNumber ||
                         TWILIO_NUMBER;
    const businessHours = businessData.businessInfo?.hours || '9am-5pm Monday-Friday';
    const businessDescription = businessData.businessInfo?.description || '';

    // Log important business fields
    console.log('Business name:', businessName);
    console.log('Business phone:', businessPhone);
    console.log('Business hours:', businessHours);

    // Construct the commonQuestions array
    const commonQuestions = businessData.commonQuestions || [];

    // Get initial message if available
    const introMessage = businessData.introMessage || "Sorry I missed your call! I'm currently with a customer. How can I help you today?";

    // Create the request payload
    const payload = {
      userId,
      phoneNumber: businessPhone,
      customerNumber: phoneNumber,
      businessName,
      businessPhone,
      businessHours,
      businessDescription,
      commonQuestions,
      introMessage,
      businessInfo: {
        ...businessData.businessInfo,
        name: businessName,
        phoneNumber: businessPhone,
        hours: businessHours,
        description: businessDescription
      }
    };

    console.log('Sending conversation request with payload:', payload);

    // Define API endpoint
    const VERTEX_AI_ENDPOINT = 'https://us-central1-auto-text-back-web-app.cloudfunctions.net/vertexAiConversation';
    console.log('Using Vertex AI endpoint:', VERTEX_AI_ENDPOINT);

    // Make the API request with retry logic
    const response = await retryRequest(
      () => api.post(VERTEX_AI_ENDPOINT, payload),
      3,  // max retries
      1000 // delay between retries
    );

    if (!response || !response.data) {
      throw new Error('Invalid response from server');
    }

    console.log('Conversation created:', response.data);

    // Handle different response formats
    if (response.data.conversationId) {
      // Standard format with conversationId directly in the response
      return response.data;
    } else if (response.data.success && response.data.response) {
      // Alternative format where response is nested
      return {
        conversationId: response.data.response.conversationId || `local-${Date.now()}`,
        initialMessage: response.data.response.text || introMessage
      };
    } else {
      // Fallback for any other format
      console.warn('Unexpected response format:', response.data);
      return {
        conversationId: `local-${Date.now()}`,
        initialMessage: introMessage
      };
    }
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

/**
 * Rephrases an answer to make it more conversational and natural
 * @param {string} answer - The original answer from the database
 * @param {string} question - The user's question
 * @returns {string} - A rephrased, conversational response
 */
function rephraseAnswer(answer, question) {
  if (!answer) return getFallbackMessage();

  // Clean up the answer
  const cleanAnswer = answer.replace(/\n+/g, ' ').trim();
  const lowerQuestion = question.toLowerCase().trim();

  // AC/Repair specific questions
  if ((lowerQuestion.includes('ac') || lowerQuestion.includes('air') || lowerQuestion.includes('broke')) &&
      (lowerQuestion.includes('when') || lowerQuestion.includes('come') ||
       lowerQuestion.includes('fix') || lowerQuestion.includes('repair') ||
       lowerQuestion.includes('look'))) {

    // Use the answer directly, it should be service-related
    return cleanAnswer;
  }

  // Appointment/Service questions
  if (lowerQuestion.includes('appoint') ||
      lowerQuestion.includes('schedul') ||
      lowerQuestion.includes('book') ||
      lowerQuestion.includes('come') ||
      lowerQuestion.includes('fix') ||
      lowerQuestion.includes('repair') ||
      lowerQuestion.includes('service')) {

    return `${cleanAnswer}`;
  }

  // Hours questions
  if (lowerQuestion.includes('hour') ||
      lowerQuestion.includes('open') ||
      lowerQuestion.includes('close') ||
      lowerQuestion.includes('time')) {

    return `We're open ${cleanAnswer}`;
  }

  // Default response
  return cleanAnswer;
}

/**
 * Sends a message to the AI
 * @param {string} conversationId - The conversation ID
 * @param {string} message - The message to send
 * @param {string} userId - The user ID
 * @param {Object} businessData - The business data
 * @returns {Promise<Object>} - The response data
 */
export const sendMessage = async (conversationId, message, userId, businessData) => {
  try {
    console.log(`Sending message to conversation ${conversationId}`);

    // Validate business data
    if (!businessData) {
      console.error('No business data provided');
      businessData = {
        businessInfo: {
          name: 'Your Business',
          hours: '9am-5pm Monday-Friday'
        },
        phoneNumber: DEFAULT_PHONE
      };
      console.log('Using default business data');
    }

    // Use the correct business phone number
    const businessPhone = businessData.phoneData?.phoneNumber ||
                         businessData.phoneNumber ||
                         businessData.businessInfo?.phoneNumber ||
                         DEFAULT_PHONE;

    console.log('Using business phone number:', businessPhone);

    // Extract AI training data
    const commonQuestions = businessData.commonQuestions || [];

    console.log('Common Q&A pairs:', commonQuestions);

    // DIRECT FIX: Check for AC or service-related questions first (with higher priority)
    const lowerMessage = message.toLowerCase();
    if ((lowerMessage.includes('ac') || lowerMessage.includes('air condition') || lowerMessage.includes('broke')) &&
        (lowerMessage.includes('when') || lowerMessage.includes('come') ||
         lowerMessage.includes('look at') || lowerMessage.includes('fix') || lowerMessage.includes('take a look'))) {

      console.log('DIRECT FIX: Detected AC or repair service question, using service response');

      // Look for a specific AC or service-related answer in commonQuestions
      let serviceAnswer = getFallbackMessage(); // Use fallback message as default

      // First try to find an exact match for AC questions
      for (const qa of commonQuestions) {
        const lowerQuestion = qa.question.toLowerCase();
        if (lowerQuestion.includes('ac') ||
            lowerQuestion.includes('air') ||
            lowerQuestion.includes('fix') ||
            lowerQuestion.includes('repair')) {
          console.log('Found AC-specific answer:', qa.answer);
          serviceAnswer = qa.answer;
          break;
        }
      }

      // If no AC-specific answer, fall back to general appointment questions
      if (serviceAnswer === getFallbackMessage()) {
        for (const qa of commonQuestions) {
          const lowerQuestion = qa.question.toLowerCase();
          if (lowerQuestion.includes('appointment') ||
              lowerQuestion.includes('schedule') ||
              lowerQuestion.includes('book') ||
              lowerQuestion.includes('come')) {
            console.log('Found general appointment answer:', qa.answer);
            serviceAnswer = qa.answer;
            break;
          }
        }
      }

      return {
        data: {
          reply: serviceAnswer,
          messageId: `direct-${Date.now()}`
        }
      };
    }

    // DIRECT FIX: Check for service-related questions immediately
    if ((lowerMessage.includes('service') || lowerMessage.includes('repair') || lowerMessage.includes('fix')) &&
        (lowerMessage.includes('when') || lowerMessage.includes('come') ||
         lowerMessage.includes('look at') || lowerMessage.includes('schedule'))) {

      console.log('DIRECT FIX: Detected service scheduling question, using service response');

      // Look for a service scheduling answer in commonQuestions
      let serviceAnswer = getFallbackMessage(); // Use fallback message as default

      // First try to find specific service-related answers
      for (const qa of commonQuestions) {
        const lowerQuestion = qa.question.toLowerCase();
        if (lowerQuestion.includes('service') ||
            lowerQuestion.includes('repair') ||
            lowerQuestion.includes('fix')) {
          console.log('Found specific service answer:', qa.answer);
          serviceAnswer = qa.answer;
          break;
        }
      }

      // If no specific service answer, fall back to general appointment questions
      if (serviceAnswer === getFallbackMessage()) {
        for (const qa of commonQuestions) {
          const lowerQuestion = qa.question.toLowerCase();
          if (lowerQuestion.includes('appoint') ||
              lowerQuestion.includes('schedul') ||
              lowerQuestion.includes('book') ||
              lowerQuestion.includes('come')) {
            console.log('Found general appointment answer:', qa.answer);
            serviceAnswer = qa.answer;
            break;
          }
        }
      }

      return {
        data: {
          reply: serviceAnswer,
          messageId: `direct-${Date.now()}`
        }
      };
    }

    // DIRECT FIX: Check for service timing questions (when are you coming, what time, etc.)
    if ((lowerMessage.includes('time') &&
         (lowerMessage.includes('coming') || lowerMessage.includes('arrive') ||
          lowerMessage.includes('be there') || lowerMessage.includes('show up') ||
          lowerMessage.includes('today') || lowerMessage.includes('tomorrow'))) ||
        (lowerMessage.includes('when') &&
         (lowerMessage.includes('coming') || lowerMessage.includes('arrive') ||
          lowerMessage.includes('be there') || lowerMessage.includes('show up') ||
          lowerMessage.includes('today') || lowerMessage.includes('tomorrow')))) {

      console.log('DIRECT FIX: Detected service timing question, using service response');

      // Look for a service timing answer in commonQuestions
      let timingAnswer = getFallbackMessage(); // Use fallback message as default

      // First try to find specific timing-related answers
      for (const qa of commonQuestions) {
        const lowerQuestion = qa.question.toLowerCase();
        if (lowerQuestion.includes('time') &&
            (lowerQuestion.includes('coming') || lowerQuestion.includes('today') ||
             lowerQuestion.includes('arrive') || lowerQuestion.includes('when'))) {
          console.log('Found specific timing answer:', qa.answer);
          timingAnswer = qa.answer;
          break;
        }
      }

      // If no specific timing answer, fall back to general appointment questions
      if (timingAnswer === getFallbackMessage()) {
        for (const qa of commonQuestions) {
          const lowerQuestion = qa.question.toLowerCase();
          if (lowerQuestion.includes('appoint') ||
              lowerQuestion.includes('schedul') ||
              lowerQuestion.includes('book') ||
              lowerQuestion.includes('come')) {
            console.log('Found general appointment answer for timing:', qa.answer);
            timingAnswer = qa.answer;
            break;
          }
        }
      }

      return {
        data: {
          reply: timingAnswer,
          messageId: `direct-${Date.now()}`
        }
      };
    }

    // DIRECT FIX: Check for business hours-related questions (more specific to avoid service timing conflicts)
    if (lowerMessage.includes('hour') ||
        (lowerMessage.includes('open') && !lowerMessage.includes('opening')) ||
        lowerMessage.includes('close') ||
        (lowerMessage.includes('when') && lowerMessage.includes('open')) ||
        // Only catch "time" when it's clearly about business hours, not service timing
        (lowerMessage.includes('time') &&
         (lowerMessage.includes('open') || lowerMessage.includes('close') ||
          lowerMessage.includes('business') || lowerMessage.includes('store') ||
          lowerMessage.includes('office') || lowerMessage.includes('available')))) {

      // Find the hours answer from commonQuestions
      let hoursAnswer = getFallbackMessage(); // Use fallback message as default

      for (const qa of commonQuestions) {
        if (qa.question.toLowerCase().includes('hour') ||
            qa.question.toLowerCase().includes('open')) {
          hoursAnswer = qa.answer;
          break;
        }
      }

      // If we didn't find a specific hours answer, check if we have any hours in the business data
      if (hoursAnswer === getFallbackMessage() && businessData && businessData.businessInfo && businessData.businessInfo.hours) {
        hoursAnswer = businessData.businessInfo.hours;
      }

      console.log('DIRECT FIX: Detected hours question, using conversational response');

      return {
        data: {
          reply: `We're open ${hoursAnswer}`,
          messageId: `direct-${Date.now()}`
        }
      };
    }

    // DIRECT FIX: Check for "how much" or service cost questions
    if ((lowerMessage.includes('how much') ||
         (lowerMessage.includes('how') && lowerMessage.includes('much')) ||
         lowerMessage.includes('price') || lowerMessage.includes('cost') ||
         lowerMessage.includes('fee') || lowerMessage.includes('charge') ||
         // Also check for massage-specific terms
         (lowerMessage.includes('how') && lowerMessage.includes('massage')) ||
         (lowerMessage.includes('massage') && lowerMessage.includes('price')) ||
         (lowerMessage.includes('massage') && lowerMessage.includes('cost')) ||
         (lowerMessage.includes('massage') && lowerMessage.includes('much')) ||
         // Also check for message/messaging (common misspelling of massage)
         (lowerMessage.includes('message') && lowerMessage.includes('price')) ||
         (lowerMessage.includes('message') && lowerMessage.includes('cost')) ||
         (lowerMessage.includes('message') && lowerMessage.includes('much')))) {

      // Find the service cost answer from commonQuestions
      let costAnswer = getFallbackMessage(); // Use fallback message as default

      // First look for exact "how much" questions
      for (const qa of commonQuestions) {
        const lowerQuestion = qa.question.toLowerCase();
        if ((lowerQuestion.includes('how much') &&
             (lowerQuestion.includes('cost') ||
              lowerQuestion.includes('fee') ||
              lowerQuestion.includes('charge') ||
              lowerQuestion.includes('price'))) ||
            lowerQuestion.includes('how much for')) {
          console.log('Found exact price question match:', qa.question);
          costAnswer = qa.answer;
          break;
        }
      }

      // If no exact match, look for any cost-related questions
      if (costAnswer === getFallbackMessage()) {
        for (const qa of commonQuestions) {
          const lowerQuestion = qa.question.toLowerCase();
          if (lowerQuestion.includes('cost') ||
              lowerQuestion.includes('fee') ||
              lowerQuestion.includes('charge') ||
              lowerQuestion.includes('price') ||
              lowerQuestion.includes('service call')) {
            console.log('Found general price question match:', qa.question);
            costAnswer = qa.answer;
            break;
          }
        }
      }

      console.log('DIRECT FIX: Detected service cost question, using direct response');

      return {
        data: {
          reply: costAnswer,
          messageId: `direct-${Date.now()}`
        }
      };
    }

    // Try to match the question locally first for quicker response
    // Use the improved AI matching algorithm
    console.log('Using improved AI matching algorithm');

    // First check if the question is business-related
    const isBusinessRelated = isBusinessRelatedQuestion(message);
    console.log(`Is business-related: ${isBusinessRelated}`);

    if (!isBusinessRelated) {
      const fallbackMessage = getFallbackMessage();
      console.log('Question is not business-related, using fallback message:', fallbackMessage);

      // Make sure we're returning a valid response
      return {
        data: {
          reply: fallbackMessage,
          messageId: `fallback-${Date.now()}`
        }
      };
    }

    // Find the best matching answer using the improved algorithm
    const bestMatch = findBestMatchingAnswer(message, commonQuestions);

    // Use the best match if found, otherwise use the fallback message
    if (bestMatch) {
      console.log('Found matching answer:', bestMatch);

      // Rephrase the response to sound more conversational
      const rephrasedResponse = rephraseAnswer(bestMatch, message);
      console.log('Rephrased response:', rephrasedResponse);

      return {
        data: {
          reply: rephrasedResponse,
          messageId: `local-${Date.now()}`
        }
      };
    }

    // Return fallback message if no good match found
    console.log('No good match found, using fallback message');
    return {
      data: {
        reply: getFallbackMessage(),
        messageId: `fallback-${Date.now()}`
      }
    };

    /* COMMENT: The code below will no longer be reached after adding the fallback above,
       but we're keeping it for now in case we want to re-enable server calls later */

    // Prepare business information
    const businessInfo = {
      ...businessData.businessInfo || {},
      name: businessData.businessName || businessData.businessInfo?.name || 'Your Business',
      description: businessData.businessDescription || businessData.businessInfo?.description || '',
      address: businessData.businessInfo?.address || '',
      phoneNumber: businessPhone,
      hours: businessData.businessInfo?.hours || '9am-5pm Monday-Friday',
      aiTraining: {
        questionAnswerPairs: commonQuestions
      }
    };

    console.log('Sending business info:', businessInfo);

    try {
      // Try to get response from server if no local match
      const response = await api.post(`/vertex-ai/conversation/${conversationId}/message`, {
        message,
        userId,
        businessInfo: businessInfo,
        commonQuestions: commonQuestions,
        businessPhone: businessPhone
      });

      return response.data;
    } catch (serverError) {
      console.error('Server error, using fallback:', serverError);

      // If server fails, return fallback message
      return {
        data: {
          reply: getFallbackMessage(),
          messageId: `fallback-${Date.now()}`
        }
      };
    }
  } catch (error) {
    console.error('Error sending message:', error);
    // Return fallback response instead of throwing
    return {
      data: {
        reply: getFallbackMessage(),
        messageId: `error-${Date.now()}`
      }
    };
  }
};

/**
 * Sends a message to the AI assistant and gets a response
 * @param {string} message - The message to send to the AI
 * @param {string} conversationId - Optional conversation ID for continuing a conversation
 * @returns {Promise<string>} - The AI's response
 */
export const sendMessageToAI = async (message, conversationId = null) => {
  try {
    // Get the current user
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get the user's business info
    const businessDoc = await getDoc(doc(db, 'businesses', user.uid));

    if (!businessDoc.exists()) {
      throw new Error('Business information not found');
    }

    const businessData = businessDoc.data();
    console.log('Business data:', businessData);

    // Prepare the request data
    const requestData = {
      userId: user.uid,
      message,
      conversationId,
      businessInfo: businessData.businessInfo || {},
      commonQuestions: businessData.commonQuestions || [],
      initialMessage: businessData.initialMessage || "Sorry I missed your call! I'm currently with a customer. How can I help you today?",
      closingMessage: businessData.closingMessage || "Thank you for reaching out! I'll call you back shortly to assist further."
    };

    // Get the auth token
    const token = await user.getIdToken();

    try {
      // Send the request to the backend
      const response = await axios.post(`/api/vertex-ai/conversation/${conversationId || 'new'}/message`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Return the AI's response
      return response.data.reply || response.data.response;
    } catch (error) {
      console.error('Error sending message to AI:', error);

      // Return a fallback response for the UI
      return getFallbackMessage();
    }
  } catch (error) {
    console.error('Error in sendMessageToAI:', error);
    throw error;
  }
};

/**
 * Creates a new conversation with the AI assistant
 * @returns {Promise<string>} - The conversation ID
 */
export const createAIConversation = async () => {
  try {
    // Get the current user
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get the user's business info
    const businessDoc = await getDoc(doc(db, 'businesses', user.uid));

    if (!businessDoc.exists()) {
      throw new Error('Business information not found');
    }

    const businessData = businessDoc.data();

    // Get the auth token
    const token = await user.getIdToken();

    // Create a new conversation
    const response = await axios.post('/api/vertex-ai/conversation', {
      userId: user.uid,
      businessInfo: businessData.businessInfo
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    // Return the conversation ID
    return response.data.conversationId;
  } catch (error) {
    console.error('Error creating AI conversation:', error);
    throw error;
  }
};

// Test the rephrasing function with sample questions (this will run when the module loads)
try {
  console.log('TESTING REPHRASING FUNCTION:');

  // Test Case 1: Service question
  const testServiceQuestion = "When can you come to fix my problem?";
  const testServiceAnswer = "I will give you a call shortly to confirm a time and date that works for you. Thank you.";
  const rephrasedServiceAnswer = rephraseAnswer(testServiceAnswer, testServiceQuestion);
  console.log('Test 1 - Service Appointment:');
  console.log('Question:', testServiceQuestion);
  console.log('Original answer:', testServiceAnswer);
  console.log('Rephrased answer:', rephrasedServiceAnswer);

  // Test Case 2: Hours question
  const testHoursQuestion = "What are your business hours?";
  const testHoursAnswer = "Monday - Saturday 7-5 PM.";
  const rephrasedHoursAnswer = rephraseAnswer(testHoursAnswer, testHoursQuestion);
  console.log('\nTest 2 - Hours:');
  console.log('Question:', testHoursQuestion);
  console.log('Original answer:', testHoursAnswer);
  console.log('Rephrased answer:', rephrasedHoursAnswer);

  // Test Case 3: AC repair question
  const testACQuestion = "I need my AC looked at when can you come?";
  const testACAnswer = "If they call in AM we normally can get someone out that day. If they call in PM we can get someone out next morning or afternoon.";
  const rephrasedACAnswer = rephraseAnswer(testACAnswer, testACQuestion);
  console.log('\nTest 3 - AC Repair:');
  console.log('Question:', testACQuestion);
  console.log('Original answer:', testACAnswer);
  console.log('Rephrased answer:', rephrasedACAnswer);

  // Test Case 4: General question
  const testGeneralQuestion = "Do you accept credit cards?";
  const testGeneralAnswer = "Yes, we accept all major credit cards.";
  const rephrasedGeneralAnswer = rephraseAnswer(testGeneralAnswer, testGeneralQuestion);
  console.log('\nTest 4 - General Question:');
  console.log('Question:', testGeneralQuestion);
  console.log('Original answer:', testGeneralAnswer);
  console.log('Rephrased answer:', rephrasedGeneralAnswer);

} catch (error) {
  console.error('Error testing rephrasing function:', error);
}
